"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SequencePanel = void 0;
const react_1 = __importStar(require("react"));
const material_1 = require("@mui/material");
const mui_1 = require("tss-react/mui");
const react_intersection_observer_1 = require("react-intersection-observer");
const copy_to_clipboard_1 = __importDefault(require("copy-to-clipboard"));
const util_1 = require("../util");
const configuration_1 = require("../configuration");
const util_2 = require("./util");
const useStyles = (0, mui_1.makeStyles)()(theme => ({
    button: {
        margin: theme.spacing(1),
    },
}));
// note that these are currently put into the style section instead of being
// defined in classes to aid copy and paste to an external document e.g. word
const proteinColor = 'rgb(220,160,220)';
const intronColor = undefined;
const cdsColor = 'rgb(220,220,180)';
const updownstreamColor = 'rgba(250,200,200)';
const utrColor = 'rgb(200,240,240)';
function GeneCDS({ cds, sequence }) {
    return react_1.default.createElement("span", { style: { background: cdsColor } }, (0, util_2.stitch)(cds, sequence));
}
function GeneProtein({ cds, sequence, codonTable, }) {
    const str = (0, util_2.stitch)(cds, sequence);
    let protein = '';
    for (let i = 0; i < str.length; i += 3) {
        // use & symbol for undefined codon, or partial slice
        protein += codonTable[str.slice(i, i + 3)] || '&';
    }
    return react_1.default.createElement("span", { style: { background: proteinColor } }, protein);
}
function GenecDNA({ utr, cds, exons, sequence, upstream, downstream, includeIntrons, collapseIntron, }) {
    const chunks = cds.length
        ? [...cds, ...utr].sort((a, b) => a.start - b.start)
        : exons;
    return (react_1.default.createElement(react_1.default.Fragment, null,
        upstream ? (react_1.default.createElement("span", { style: { background: updownstreamColor } }, upstream)) : null,
        chunks
            .filter(f => f.start !== f.end)
            .map((chunk, index) => {
            var _a;
            const intron = sequence.slice(chunk.end, (_a = chunks[index + 1]) === null || _a === void 0 ? void 0 : _a.start);
            return (react_1.default.createElement(react_1.default.Fragment, { key: JSON.stringify(chunk) },
                react_1.default.createElement("span", { style: {
                        background: chunk.type === 'CDS' ? cdsColor : utrColor,
                    } }, sequence.slice(chunk.start, chunk.end)),
                includeIntrons && index < chunks.length - 1 ? (react_1.default.createElement("span", { style: { background: intronColor } }, collapseIntron && intron.length > 20
                    ? `${intron.slice(0, 10)}...${intron.slice(-10)}`
                    : intron)) : null));
        }),
        downstream ? (react_1.default.createElement("span", { style: { background: updownstreamColor } }, downstream)) : null));
}
exports.SequencePanel = react_1.default.forwardRef(({ feature, mode, sequence: { seq: sequence, upstream = '', downstream = '' }, }, ref) => {
    const { subfeatures } = feature;
    const codonTable = (0, util_1.generateCodonTable)(util_1.defaultCodonTable);
    if (!subfeatures) {
        return null;
    }
    const children = subfeatures
        .sort((a, b) => a.start - b.start)
        .map(sub => ({
        ...sub,
        start: sub.start - feature.start,
        end: sub.end - feature.start,
    }));
    // we filter duplicate entries in cds and exon lists duplicate entries may be
    // rare but was seen in Gencode v36 track NCList, likely a bug on GFF3 or
    // probably worth ignoring here (produces broken protein translations if
    // included)
    //
    // position 1:224,800,006..225,203,064 gene ENSG00000185842.15 first
    // transcript ENST00000445597.6
    //
    // http://localhost:3000/?config=test_data%2Fconfig.json&session=share-FUl7G1isvF&password=HXh5Y
    let cds = (0, util_2.dedupe)(children.filter(sub => sub.type === 'CDS'));
    let utr = (0, util_2.dedupe)(children.filter(sub => sub.type.match(/utr/i)));
    let exons = (0, util_2.dedupe)(children.filter(sub => sub.type === 'exon'));
    if (!utr.length && cds.length && exons.length) {
        utr = (0, util_2.calculateUTRs)(cds, exons);
    }
    if (feature.strand === -1) {
        // doing this in a single assignment is needed because downstream and
        // upstream are swapped so this avoids a temp variable
        ;
        [sequence, upstream, downstream] = [
            (0, util_1.revcom)(sequence),
            (0, util_1.revcom)(downstream),
            (0, util_1.revcom)(upstream),
        ];
        cds = (0, util_2.revlist)(cds, sequence.length);
        exons = (0, util_2.revlist)(exons, sequence.length);
        utr = (0, util_2.revlist)(utr, sequence.length);
    }
    return (react_1.default.createElement("div", { ref: ref, "data-testid": "sequence_panel" },
        react_1.default.createElement("div", { style: {
                fontFamily: 'monospace',
                wordWrap: 'break-word',
                fontSize: 12,
                maxWidth: 600,
            } },
            `>${feature.name || feature.id || 'unknown'}-${mode}\n`,
            mode === 'cds' ? (react_1.default.createElement(GeneCDS, { cds: cds, sequence: sequence })) : mode === 'cdna' ? (react_1.default.createElement(GenecDNA, { exons: exons, cds: cds, utr: utr, sequence: sequence })) : mode === 'protein' ? (react_1.default.createElement(GeneProtein, { cds: cds, codonTable: codonTable, sequence: sequence })) : mode === 'gene' ? (react_1.default.createElement(GenecDNA, { exons: exons, cds: cds, utr: utr, sequence: sequence, includeIntrons: true })) : mode === 'gene_collapsed_intron' ? (react_1.default.createElement(GenecDNA, { exons: exons, cds: cds, sequence: sequence, utr: utr, includeIntrons: true, collapseIntron: true })) : mode === 'gene_updownstream' ? (react_1.default.createElement(GenecDNA, { exons: exons, cds: cds, sequence: sequence, utr: utr, upstream: upstream, downstream: downstream, includeIntrons: true })) : mode === 'gene_updownstream_collapsed_intron' ? (react_1.default.createElement(GenecDNA, { exons: exons, cds: cds, sequence: sequence, utr: utr, upstream: upstream, downstream: downstream, includeIntrons: true, collapseIntron: true })) : (react_1.default.createElement("div", null, "Unknown type")))));
});
// display the stitched-together sequence of a gene's CDS, cDNA, or protein
// sequence. this is a best effort and weird genomic phenomena could lead these
// to not be 100% accurate
function SequenceFeatureDetails({ model, feature }) {
    var _a;
    const { classes } = useStyles();
    const parentFeature = feature;
    const hasCDS = (_a = parentFeature.subfeatures) === null || _a === void 0 ? void 0 : _a.find(sub => sub.type === 'CDS');
    const seqPanelRef = (0, react_1.useRef)(null);
    const { ref, inView } = (0, react_intersection_observer_1.useInView)();
    const [sequence, setSequence] = (0, react_1.useState)();
    const [error, setError] = (0, react_1.useState)();
    const [mode, setMode] = (0, react_1.useState)(hasCDS ? 'cds' : 'cdna');
    const [copied, setCopied] = (0, react_1.useState)(false);
    const [copiedHtml, setCopiedHtml] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        var _a;
        let finished = false;
        if (!model || !inView) {
            return () => { };
        }
        const { assemblyManager, rpcManager } = (0, util_1.getSession)(model);
        const [assemblyName] = ((_a = model.view) === null || _a === void 0 ? void 0 : _a.assemblyNames) || [];
        async function fetchSeq(start, end, refName) {
            const assembly = await assemblyManager.waitForAssembly(assemblyName);
            if (!assembly) {
                throw new Error('assembly not found');
            }
            const sessionId = 'getSequence';
            const feats = await rpcManager.call(sessionId, 'CoreGetFeatures', {
                adapterConfig: (0, configuration_1.getConf)(assembly, ['sequence', 'adapter']),
                sessionId,
                regions: [
                    {
                        start,
                        end,
                        refName: assembly.getCanonicalRefName(refName),
                    },
                ],
            });
            const [feat] = feats;
            if (!feat) {
                throw new Error(`sequence not found for feature with refName:${refName}`);
            }
            return feat.get('seq');
        }
        ;
        (async () => {
            try {
                const { start, end, refName } = feature;
                const seq = await fetchSeq(start, end, refName);
                const up = await fetchSeq(Math.max(0, start - 500), start, refName);
                const down = await fetchSeq(end, end + 500, refName);
                if (!finished) {
                    setSequence({ seq, upstream: up, downstream: down });
                }
            }
            catch (e) {
                setError(e);
            }
        })();
        return () => {
            finished = true;
        };
    }, [feature, inView, model]);
    const loading = !sequence;
    return (react_1.default.createElement("div", { ref: ref },
        react_1.default.createElement(material_1.FormControl, null,
            react_1.default.createElement(material_1.Select, { value: mode, onChange: event => setMode(event.target.value) },
                hasCDS ? react_1.default.createElement(material_1.MenuItem, { value: "cds" }, "CDS") : null,
                hasCDS ? react_1.default.createElement(material_1.MenuItem, { value: "protein" }, "Protein") : null,
                react_1.default.createElement(material_1.MenuItem, { value: "gene" }, "Gene w/ introns"),
                react_1.default.createElement(material_1.MenuItem, { value: "gene_collapsed_intron" }, "Gene w/ 10bp of intron"),
                react_1.default.createElement(material_1.MenuItem, { value: "gene_updownstream" }, "Gene w/ 500bp up+down stream"),
                react_1.default.createElement(material_1.MenuItem, { value: "gene_updownstream_collapsed_intron" }, "Gene w/ 500bp up+down stream w/ 10bp intron"),
                react_1.default.createElement(material_1.MenuItem, { value: "cdna" }, "cDNA"))),
        react_1.default.createElement(material_1.Button, { className: classes.button, type: "button", variant: "contained", onClick: () => {
                const ref = seqPanelRef.current;
                if (ref) {
                    (0, copy_to_clipboard_1.default)(ref.textContent || '', { format: 'text/plain' });
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1000);
                }
            } }, copied ? 'Copied to clipboard!' : 'Copy as plaintext'),
        react_1.default.createElement(material_1.Tooltip, { title: "Note that 'Copy as HTML' can retain the colors but cannot be pasted into some programs like notepad that only expect plain text" },
            react_1.default.createElement(material_1.Button, { className: classes.button, type: "button", variant: "contained", onClick: () => {
                    const ref = seqPanelRef.current;
                    if (ref) {
                        (0, copy_to_clipboard_1.default)(ref.innerHTML, { format: 'text/html' });
                        setCopiedHtml(true);
                        setTimeout(() => setCopiedHtml(false), 1000);
                    }
                } }, copiedHtml ? 'Copied to clipboard!' : 'Copy as HTML')),
        react_1.default.createElement("div", { "data-testid": "feature_sequence" }, error ? (react_1.default.createElement(material_1.Typography, { color: "error" }, `${error}`)) : loading ? (react_1.default.createElement("div", null, "Loading gene sequence...")) : sequence ? (react_1.default.createElement(exports.SequencePanel, { ref: seqPanelRef, feature: parentFeature, mode: mode, sequence: sequence })) : (react_1.default.createElement("div", null, "No sequence found")))));
}
exports.default = SequenceFeatureDetails;
